import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPageModelSE0 } from 'store/pageModel';
import { selectTopCategories } from 'containers/catalogue/catalogue/selectors';
import { selectServices } from 'containers/instore/instore-service/selectors';
import { selectConfigurators } from 'containers/categories-listing/selectors';
import { getMaxPrice, getMinPrice, getOfferData, getPrices } from './utils';

export type SEOOfferSchemaProps = {
	canonicalUrl: string;
	offerCount: number;
	lowPrice: number;
	highPrice: number;
};

export const SEOOfferSchema = ({
	canonicalUrl,
	offerCount,
	lowPrice,
	highPrice,
}: SEOOfferSchemaProps) => (
	<div itemProp="offers" itemScope itemType="https://schema.org/AggregateOffer">
		<link itemProp="url" href={canonicalUrl} />
		<link itemProp="availability" href="https://schema.org/InStock" />
		<meta itemProp="priceCurrency" content="RUB" />
		<meta itemProp="offerCount" content={`${offerCount}`} />
		<meta itemProp="lowPrice" content={`${lowPrice}`} />
		<meta itemProp="highPrice" content={`${highPrice}`} />
	</div>
);

export const SEOProductSchema = () => {
	const pageModelSEO = useSelector(selectPageModelSE0);
	const categories = useSelector(selectTopCategories);
	const services = useSelector(selectServices);

	const name = pageModelSEO?.h1 || '';
	const productName = services.length ? name.split(' в ')[0] : name;
	const description = pageModelSEO?.metadescription || '';
	const canonicalUrl = pageModelSEO?.canonicalURL || '';

	const categoriesOfferData = useMemo(() => getOfferData(categories, 'categories'), [categories]);
	const servicesOfferData = useMemo(() => getOfferData(services, 'services'), [services]);
	const offerData = useMemo(() => categoriesOfferData || servicesOfferData || null, [
		categoriesOfferData,
		servicesOfferData,
	]);

	return (
		<div itemType="https://schema.org/Product" itemScope>
			<meta itemProp="name" content={productName} />
			<meta itemProp="description" content={description} />
			{offerData && <SEOOfferSchema canonicalUrl={canonicalUrl} {...offerData} />}
		</div>
	);
};

export const SEOSLPProductSchema = () => {
	const pageModelSEO = useSelector(selectPageModelSE0);
	const configurators = useSelector(selectConfigurators);

	const name = pageModelSEO?.h1 || '';
	const productName = name;
	const description = pageModelSEO?.metadescription || '';
	const canonicalUrl = pageModelSEO?.canonicalURL || '';

	const prices = getPrices(configurators);
	const minPrice = getMinPrice(prices);
	const maxPrice = getMaxPrice(prices);

	const offerData = configurators.length
		? {
				offerCount: configurators.length,
				lowPrice: minPrice,
				highPrice: maxPrice,
		  }
		: null;

	return (
		<div itemType="https://schema.org/Product" itemScope>
			<meta itemProp="name" content={productName} />
			<meta itemProp="description" content={description} />
			{offerData && <SEOOfferSchema canonicalUrl={canonicalUrl} {...offerData} />}
		</div>
	);
};
